import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, ChevronUp, GripVertical, Info } from 'lucide-react'
import { ModalShell } from '../common/ModalShell.js'
import { cgTemplatesApi } from '../../lib/api.js'
import { CgTemplateThumbnail } from './CgTemplateThumbnail.js'
import { layerOrderChanged, moveInLayerOrder, overlayVideoLayer } from './cgLayerOrder.js'
import type { CgLayerOrderEntry, CgLayerOrderResult, CgTemplate } from '../../types/index.js'

interface Props {
  channelId: string
  /** Modèles déjà chargés par le Studio : vignettes. */
  templates: CgTemplate[]
  onClose: () => void
  onSaved: (result: CgLayerOrderResult) => void
}

interface RowProps {
  entry: CgLayerOrderEntry
  /** Layer CG que le modèle aura à ce rang une fois appliqué. */
  cgLayer: number
  template: CgTemplate | undefined
  index: number
  count: number
  disabled: boolean
  onMove: (index: number, delta: number) => void
}

const LayerOrderRow = memo(function LayerOrderRow({ entry, cgLayer, template, index, count, disabled, onMove }: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: entry.id, disabled })
  return (
    <li
      ref={setNodeRef}
      className={`cg-layer-order-row${isDragging ? ' is-dragging' : ''}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <button
        type="button"
        className="cg-layer-order-grip"
        aria-label={`Déplacer ${entry.name}`}
        title="Glisser pour changer la superposition"
        disabled={disabled}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={14} aria-hidden="true" />
      </button>
      <span className="cg-layer-order-rank">{index + 1}</span>
      <span className="cg-layer-order-thumb" aria-hidden="true"><CgTemplateThumbnail template={template} /></span>
      <span className="cg-layer-order-name" title={entry.name}>
        <strong>{entry.name}</strong>
        <small>Couche {overlayVideoLayer(cgLayer)}</small>
      </span>
      <span className="cg-layer-order-moves">
        <button type="button" className="cg-icon-button" onClick={() => onMove(index, -1)} disabled={disabled || index === 0} aria-label={`Monter ${entry.name}`} title="Monter : passe au-dessus">
          <ChevronUp size={14} aria-hidden="true" />
        </button>
        <button type="button" className="cg-icon-button" onClick={() => onMove(index, 1)} disabled={disabled || index === count - 1} aria-label={`Descendre ${entry.name}`} title="Descendre : passe en dessous">
          <ChevronDown size={14} aria-hidden="true" />
        </button>
      </span>
    </li>
  )
})

/**
 * Superposition des habillages à l'antenne : en haut = affiché au-dessus. Le
 * logo n'y figure pas, il reste toujours sous les habillages. Appliquer
 * déplace aussitôt les habillages à l'antenne (sans les relancer).
 */
export const CgLayerOrderDialog = memo(function CgLayerOrderDialog({ channelId, templates, onClose, onSaved }: Props) {
  const [initial, setInitial] = useState<CgLayerOrderEntry[] | null>(null)
  const [order, setOrder] = useState<CgLayerOrderEntry[]>([])
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    let cancelled = false
    cgTemplatesApi.layerOrder(channelId)
      .then(result => {
        if (cancelled) return
        setInitial(result.templates)
        setOrder(result.templates)
      })
      .catch((error: Error) => { if (!cancelled) setMessage(error.message || 'Superposition illisible') })
    return () => { cancelled = true }
  }, [channelId])

  const templatesById = useMemo(() => new Map(templates.map(template => [template.id, template])), [templates])
  const ids = useMemo(() => order.map(entry => entry.id), [order])
  // Les layers déjà réservés sont redistribués du dessus vers le dessous.
  const projectedLayers = useMemo(() => (initial ?? []).map(entry => entry.cgLayer).sort((a, b) => b - a), [initial])
  const changed = useMemo(() => initial !== null && layerOrderChanged(initial.map(entry => entry.id), ids), [ids, initial])
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const move = useCallback((index: number, delta: number) => {
    setOrder(current => [...moveInLayerOrder(current, index, delta)])
  }, [])
  const dragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setOrder(current => {
      const from = current.findIndex(entry => entry.id === active.id)
      const to = current.findIndex(entry => entry.id === over.id)
      return from < 0 || to < 0 ? current : arrayMove(current, from, to)
    })
  }, [])

  const apply = useCallback(async () => {
    setSaving(true)
    setMessage('')
    try {
      const result = await cgTemplatesApi.setLayerOrder(channelId, ids)
      onSaved(result)
      onClose()
    } catch (error) {
      setMessage(error instanceof Error && error.message ? error.message : 'Superposition non enregistrée')
    } finally {
      setSaving(false)
    }
  }, [channelId, ids, onClose, onSaved])

  return (
    <ModalShell title="Superposition à l’antenne" size="narrow" className="cg-settings-modal cg-layer-order-modal" onClose={onClose} closeDisabled={saving}>
      <div className="cg-settings-form">
        <p className="cg-layer-order-lead">En haut de la liste : affiché au-dessus des autres habillages. Le logo reste toujours dessous.</p>
        {initial === null && !message && <p className="cg-settings-hint" role="status">Chargement…</p>}
        {initial !== null && order.length === 0 && <p className="cg-settings-hint">Aucun habillage à ordonner.</p>}
        {order.length > 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={dragEnd}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              <ol className="cg-layer-order-list" aria-label="Habillages, du dessus vers le dessous">
                {order.map((entry, index) => (
                  <LayerOrderRow
                    key={entry.id}
                    entry={entry}
                    cgLayer={projectedLayers[index] ?? entry.cgLayer}
                    template={templatesById.get(entry.id)}
                    index={index}
                    count={order.length}
                    disabled={saving}
                    onMove={move}
                  />
                ))}
              </ol>
            </SortableContext>
          </DndContext>
        )}
        {changed && <p className="cg-settings-hint">S’applique tout de suite : les habillages à l’antenne changent de place sans être relancés.</p>}
        {message && <p className="cg-settings-error" role="alert"><Info size={13} aria-hidden="true" />{message}</p>}
      </div>
      <footer className="cg-settings-actions">
        <button type="button" className="cg-button" onClick={onClose} disabled={saving}>Annuler</button>
        <button type="button" className="cg-button is-primary" onClick={apply} disabled={saving || !changed}>
          {saving ? 'Application…' : 'Appliquer'}
        </button>
      </footer>
    </ModalShell>
  )
})
